import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Users, ArrowLeft } from 'lucide-react';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import { validateEmail, validateRequired, validateCPF } from '../utils/validators';
import Swal from 'sweetalert2';

const AssociateLogin: React.FC = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!validateRequired(formData.email)) {
      newErrors.email = 'Email ou CPF é obrigatório';
    } else if (!validateEmail(formData.email) && !validateCPF(formData.email)) {
      newErrors.email = 'Email ou CPF inválido';
    }

    // Senha não é exigida aqui: um dependente no primeiro acesso ainda não tem
    // uma. O backend informa isso e redirecionamos para a criação de senha.
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await signIn(formData, 'associate');
      navigate('/meus-parceiros');
    } catch (err: any) {
      // O associado e o dependente entram pela mesma tela — o backend resolve
      // qual dos dois é, e sinaliza quando o dependente ainda não tem senha.
      if (err.response?.data?.status === 'senha_nao_definida') {
        navigate('/dependente/criar-senha', { state: { identifier: formData.email } });
        return;
      }

      const errorMessage = err.response?.data?.message || err.message || 'Erro ao fazer login. Verifique suas credenciais.';
      const statusCode = err.response?.status;

      // Status 403 = problemas de autorização (aguardando aprovação, conta inativa, etc)
      // Status 401 = credenciais inválidas
      const isWarning = statusCode === 403;

      await Swal.fire({
        icon: isWarning ? 'warning' : 'error',
        title: isWarning ? 'Atenção' : 'Erro ao fazer login',
        text: errorMessage,
        confirmButtonText: 'OK',
        confirmButtonColor: '#10b981'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-white to-green-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Voltar</span>
        </button>

        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-8">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-gradient-to-r from-green-500 to-green-700 rounded-xl flex items-center justify-center mb-4 shadow-lg">
              <Users className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900">Área do Associado</h2>
            <p className="mt-2 text-sm text-gray-600">
              Acesse seus benefícios e economize no dia a dia
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <Input
                label="Email ou CPF"
                type="text"
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
                placeholder="seu@email.com ou CPF"
                autoComplete="email"
                disabled={loading}
              />

              <Input
                label="Senha"
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                error={errors.password}
                placeholder="Digite sua senha (deixe em branco no 1º acesso de dependente)"
                autoComplete="current-password"
                disabled={loading}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                  Lembrar-me
                </label>
              </div>

              <div className="text-sm">
                <button
                  type="button"
                  onClick={() => navigate('/associado/forgot-password')}
                  className="font-medium text-green-600 hover:text-green-500 transition-colors"
                >
                  Esqueceu a senha?
                </button>
              </div>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/dependente/criar-senha')}
                className="text-sm font-medium text-green-600 hover:text-green-500 transition-colors"
              >
                Dependente no primeiro acesso? Criar senha
              </button>
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-green-500 to-green-700 hover:from-green-600 hover:to-green-800"
              loading={loading}
              disabled={loading}
            >
              {loading ? 'Entrando...' : 'Acessar Meus Benefícios'}
            </Button>

            {/* Link para verificação de email */}
            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/verificar-email')}
                className="text-sm text-gray-600 hover:text-green-600 transition-colors"
              >
                Ainda não verificou seu email? <span className="font-medium">Clique aqui</span>
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Aproveite seus descontos</span>
              </div>
            </div>
          </div>
        </div>

        <p className="mt-4 text-center text-xs text-gray-500">
          Cartão HMG - Seu parceiro em benefícios - 2025
        </p>
      </div>
    </div>
  );
};

export default AssociateLogin;
